import React, { useCallback, useEffect, useState } from "react";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { tokenUtil } from "@acclaim/udaf-kit";

import { confirmReset, updatePassword } from "util/APIUtils";
import AuthContainer from "components/AuthContainer";
import LoadingIndicator from "components/LoadingIndicator";
import { getUser } from "modules/user";

const useStyles = makeStyles(() => ({
  margin: {
    marginBottom: "1em",
  },
  emailContainer: {
    margin: "-1em auto 0",
    maxWidth: "400px",
  },
  updateButton: {
    display: "block",
    margin: "0 auto 2em",
  },
  error: {
    color: "#f00",
    paddingBottom: "2em",
    marginTop: "-1em",
  },
}));

const ConfirmReset = ({ getUser }) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    location: { search: tokenQuerystring },
  } = history;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [showPassword, setShowPassword] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const handleAuthResponse = useCallback(
    (response) => {
      tokenUtil.setToken(response.accessToken);
      getUser();
      setLoading(false);
    },
    [getUser]
  );

  const handleAuthError = (error) => {
    setError(
      (error && error.message) ||
        "Oops! Something went wrong. Please try again!"
    );
    setLoading(false);
  };

  useEffect(() => {
    let isSubscribed = true;
    confirmReset(tokenQuerystring)
      .then((response) => {
        if (isSubscribed) {
          if (response.previousUrl) {
            setPreviousUrl(response.previousUrl);
          }
          handleAuthResponse(response);
        }
      })
      .catch((error) => {
        if (isSubscribed) {
          console.log("error is", error);
          handleAuthError(error);
        }
      });

    return () => {
      return (isSubscribed = false);
    };
  }, [handleAuthResponse, tokenQuerystring]);

  const isValid = () => {
    let passed = true;
    setValidationError(null);
    if (password !== confirmPassword) {
      passed = false;
      setValidationError("The passwords must match");
    } else if (password.length < 8) {
      passed = false;
      setValidationError("Must be at least 8 characters.");
    } else if (password.length > 128) {
      passed = false;
      setValidationError("Must be at less than 128 characters.");
    }
    return passed;
  };
  const nextLocation = () => {
    const indexOfQuerystringStart = previousUrl ? previousUrl.indexOf("?") : -1;
    const queryStringExists = indexOfQuerystringStart > -1;
    const querystring = queryStringExists
      ? previousUrl.substr(indexOfQuerystringStart)
      : null;
    const previousPath = queryStringExists
      ? previousUrl.substr(0, indexOfQuerystringStart)
      : previousUrl;
    return {
      // pathname: previousUrl.startsWith('/auth/login')
      //   || previousUrl.startsWith('/auth/signup') ?
      //   '/'
      //   : previousPath,
      pathname:
        previousUrl.startsWith("/auth/login") ||
        previousUrl.startsWith("/auth/signup") ||
        previousUrl.endsWith("/forgot-password")
          ? "/"
          : previousPath,
      search: querystring,
    };
  };

  const handlePasswordUpdate = (event) => {
    event.preventDefault();
    if (isValid()) {
      setLoading(true);
      updatePassword(password)
        .then((response) => {
          handleAuthResponse(response);
          history.push(nextLocation());
        })
        .catch(handleAuthError);
    }
  };

  return (
    <AuthContainer className={classes.margin}>
      {loading ? (
        <LoadingIndicator />
      ) : error ? (
        <p className={classes.error}>{error}</p>
      ) : (
        <>
          <form
            className={classes.root}
            noValidate
            autoComplete="off"
            onSubmit={handlePasswordUpdate}
          >
            <div className={classes.emailContainer}>
              <TextField
                className={classes.margin}
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label="New Password"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                required
              />
              <TextField
                className={classes.margin}
                error={!!validationError}
                helperText={validationError}
                type={showPassword ? "text" : "password"}
                name="passwordConfirm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={isValid}
                variant="outlined"
                label="Confirm Password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                required
              />
            </div>
            <Button
              className={classes.updateButton}
              variant="contained"
              color="primary"
              type="submit"
            >
              Update Password
            </Button>
          </form>
        </>
      )}
    </AuthContainer>
  );
};

const withConnect = connect(null, (dispatch) => {
  return bindActionCreators(
    {
      getUser,
    },
    dispatch
  );
});

export default compose(withConnect)(ConfirmReset);
